import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEntityUrl, type EntitySearchResult } from '../lib/entityUrls';
import type { EntityType } from '../components/EntityChip';
import { EntityPreviewModal } from '../components/EntityPreviewModal';
import { AttachmentPreviewModal } from '../components/AttachmentPreviewModal';
import { mimeFromFilename } from '../lib/mimeFromFilename';

// Positive allowlist of the entity types EntityPreviewModal actually knows
// how to render (its ENDPOINTS map). Anything else — including 'tag' and
// non-EntityType values like 'attachment' that can still reach this code
// at runtime (e.g. via the [[ entity-link suggestion) — falls through to
// the navigate/open-in-new-tab branch in openPreview() instead of opening
// the modal, which would throw on an unmapped endpoint lookup.
const PREVIEWABLE: EntityType[] = [
  'task', 'note', 'meeting_instance', 'meeting_series',
  'person', 'scope', 'reference_doc', 'quick_capture',
];
export function isPreviewable(type: EntityType): boolean {
  return PREVIEWABLE.includes(type);
}

interface EntityPreviewContextValue {
  /** `label` is used for attachments, whose modal needs a filename. */
  openPreview: (type: EntityType, id: string, label?: string) => void;
  closePreview: () => void;
}

const EntityPreviewContext = createContext<EntityPreviewContextValue | null>(null);

export function useEntityPreview(): EntityPreviewContextValue {
  const ctx = useContext(EntityPreviewContext);
  if (!ctx) throw new Error('useEntityPreview must be used within EntityPreviewProvider');
  return ctx;
}

export function EntityPreviewProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [target, setTarget] = useState<{ type: EntityType; id: string } | null>(null);
  // Attachments get their own modal: EntityPreviewModal is text-shaped and has
  // no endpoint for them. Previously these opened in a new browser tab, which
  // is exactly the navigate-away behaviour links are meant to avoid.
  const [fileTarget, setFileTarget] = useState<{ id: string; filename: string } | null>(null);

  const openPreview = useCallback((type: EntityType, id: string, label?: string) => {
    if ((type as string) === 'attachment') {
      setFileTarget({ id, filename: label || 'Attachment' });
      return;
    }
    if (!isPreviewable(type)) {
      const url = getEntityUrl({ entity_type: type, entity_id: id } as EntitySearchResult);
      // Some non-previewable types (e.g. 'attachment') resolve to a real
      // server download URL, not an SPA route — react-router's navigate()
      // would just hit the SPA catch-all for those. Open server URLs in a
      // new tab instead of routing through the client-side router.
      if (url.startsWith('/api/')) window.open(url, '_blank', 'noopener');
      else navigate(url);
      return;
    }
    setTarget({ type, id });
  }, [navigate]);

  const closePreview = useCallback(() => { setTarget(null); setFileTarget(null); }, []);

  return (
    <EntityPreviewContext.Provider value={{ openPreview, closePreview }}>
      {children}
      {target && <EntityPreviewModal type={target.type} id={target.id} onClose={closePreview} />}
      {fileTarget && (
        <AttachmentPreviewModal
          attachment={{
            id: fileTarget.id,
            filename: fileTarget.filename,
            // Only the filename is available here, so the type is derived from
            // its extension. An unknown extension yields '', and the modal
            // falls back to a download button rather than a wrong renderer.
            mime_type: mimeFromFilename(fileTarget.filename),
          }}
          onClose={closePreview}
        />
      )}
    </EntityPreviewContext.Provider>
  );
}
